import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { AnimatePresence, motion, type PanInfo } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  ChevronLeft,
  Home as HomeIcon,
  CircleDot,
  Users2,
  CalendarPlus,
  BookOpen,
  Newspaper,
  Podcast,
  Video,
  Music as MusicIcon,
  Sparkles,
  RefreshCw,
  Rocket,
  Heart,
  Trophy,
  ListChecks,
  Settings as SettingsIcon,
} from 'lucide-react';
import { useGql } from '../graphql.js';

type NavItem =
  | { to: string; label: string; icon: JSX.Element }
  | { action: 'fullSync'; label: string; icon: JSX.Element };

const HOME_ITEM = { to: '/', label: 'Home', icon: <HomeIcon size={20} /> } as const;

const NAV_GROUPS: { title?: string; items: NavItem[] }[] = [
  {
    title: 'Community',
    items: [
      { to: '/gatherings',  label: 'Gatherings',          icon: <CalendarPlus size={20} /> },
      { to: '/initiatives', label: 'Initiatives',         icon: <Rocket size={20} /> },
      { to: '/circles',     label: 'Circles',             icon: <CircleDot size={20} /> },
      { to: '/games',       label: 'Leagues',             icon: <Trophy size={20} /> },
      { to: '/coalitions',  label: 'Cause Coalitions',    icon: <Heart size={20} /> },
      { to: '/connections', label: 'Connections',         icon: <Users2 size={20} /> },
    ],
  },
  {
    title: 'Library',
    items: [
      { to: '/news',          label: 'News',     icon: <Newspaper size={20} /> },
      { to: '/videos',        label: 'Videos',   icon: <Video size={20} /> },
      { to: '/podcasts',      label: 'Podcasts', icon: <Podcast size={20} /> },
      { to: '/music',         label: 'Music',    icon: <MusicIcon size={20} /> },
      { to: '/library/books', label: 'Books',    icon: <BookOpen size={20} /> },
    ],
  },
  {
    title: 'My Life',
    items: [
      { to: '/my-life/bucket-list', label: 'My Bucket List', icon: <ListChecks size={20} /> },
    ],
  },
  {
    // No title — settings is its own bottom-of-drawer cluster; a heading
    // ("Account"?) would make it feel weightier than it should.
    items: [
      { to: '/settings', label: 'Settings', icon: <SettingsIcon size={20} /> },
    ],
  },
  {
    title: 'Dev',
    items: [
      { to: '/onboarding',        label: 'Onboarding',    icon: <Sparkles size={20} /> },
      { action: 'fullSync',       label: 'Full reset',    icon: <RefreshCw size={20} /> },
    ],
  },
];

const TRIGGER_FULL_RESET = /* GraphQL */ `
  mutation TriggerFullReset { triggerFullReset }
`;

/**
 * App-level injection for the Header's right-side slot on root (non-back) pages
 * and for the drawer's account block / footer. Lets apps wire in cross-cutting
 * widgets without each feature module importing them directly — which would
 * violate the no-cross-module-imports rule.
 */
interface HeaderSlots {
  trailing: ReactNode;
  drawerAccountBlock: ReactNode;
  drawerFooter: ReactNode;
  /**
   * Fallback header title used when a screen doesn't pass an explicit `title`
   * prop. The mobile app sets this from the resolved partner network at boot
   * so every screen rebrands without each Header consumer knowing.
   */
  defaultTitle: string | null;
}
const HeaderSlotsContext = createContext<HeaderSlots>({
  trailing: null,
  drawerAccountBlock: null,
  drawerFooter: null,
  defaultTitle: null,
});

export function MobileHeaderProvider({
  trailing = null,
  drawerAccountBlock = null,
  drawerFooter = null,
  defaultTitle = null,
  children,
}: {
  trailing?: ReactNode;
  drawerAccountBlock?: ReactNode;
  drawerFooter?: ReactNode;
  defaultTitle?: string | null;
  children: ReactNode;
}) {
  return (
    <HeaderSlotsContext.Provider value={{ trailing, drawerAccountBlock, drawerFooter, defaultTitle }}>
      {children}
    </HeaderSlotsContext.Provider>
  );
}

export interface HeaderProps {
  /** Shown centred in the header bar. Defaults to "Underlings". */
  title?: string;
  /**
   * Logical parent route. When set, the leading button becomes a back arrow
   * that pops history (so the UI button and swipe-back gesture behave
   * identically). On mount, if this screen was the first history entry
   * (deep-link / PWA restore), the parent is seeded so back still works.
   */
  backTo?: string;
  /** Escape hatch for custom back behaviour (overrides backTo). */
  onBack?: () => void;
  /** Right-side slot — typically icon buttons (Notifications, Messages). */
  trailing?: ReactNode;
}

export function Header({ title, backTo, onBack, trailing }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDev, setShowDev] = useState(false);
  const [syncState, setSyncState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { trailing: defaultTrailing, drawerAccountBlock, drawerFooter, defaultTitle } = useContext(HeaderSlotsContext);
  const resolvedTitle = title ?? defaultTitle ?? 'Underlings';
  const gql = useGql();

  const runFullSync = async () => {
    if (syncState === 'running') return;
    setSyncState('running');
    try {
      // Wipes all per-user state (memberships, decisions, media history,
      // question answers, embeddings) then kicks off a fresh LinkedIn sync.
      await gql.request(TRIGGER_FULL_RESET);
      setSyncState('done');
      setTimeout(() => setSyncState('idle'), 2000);
    } catch (err) {
      console.error('[dev] full sync failed', err);
      setSyncState('error');
      setTimeout(() => setSyncState('idle'), 3000);
    }
  };

  const handleTitleTap = () => {
    tapCountRef.current += 1;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    if (tapCountRef.current >= 3) {
      setShowDev((v) => !v);
      tapCountRef.current = 0;
      return;
    }
    tapTimerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 600);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const showBack = !!(onBack || backTo);
  // Only root pages (no back button) get the injected default trailing.
  const resolvedTrailing = trailing ?? (showBack ? null : defaultTrailing);

  // Seed the parent into history when this screen is the first entry.
  // After this, navigate(-1) (and the browser swipe-back gesture) both pop
  // to backTo as expected.
  useEffect(() => {
    if (!backTo || onBack) return;
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) return;
    const here = location.pathname + location.search + location.hash;
    navigate(backTo, { replace: true });
    navigate(here);
    // Run once per screen mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  // Edge swipe to open: a touchstart within EDGE_WIDTH of the left edge arms a
  // tracker that opens the menu once horizontal travel exceeds OPEN_THRESHOLD
  // and the gesture is clearly horizontal (not a vertical scroll).
  const edgeTouchRef = useRef<{ x: number; y: number; armed: boolean } | null>(null);
  const onEdgeTouchStart = (e: React.TouchEvent) => {
    if (menuOpen) return;
    const t = e.touches[0];
    if (!t) return;
    edgeTouchRef.current = { x: t.clientX, y: t.clientY, armed: true };
  };
  const onEdgeTouchMove = (e: React.TouchEvent) => {
    const start = edgeTouchRef.current;
    if (!start || !start.armed) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dy) > 20 && Math.abs(dy) > Math.abs(dx)) {
      edgeTouchRef.current = null;
      return;
    }
    if (dx > 50) {
      setMenuOpen(true);
      edgeTouchRef.current = null;
    }
  };
  const onEdgeTouchEnd = () => {
    edgeTouchRef.current = null;
  };

  // Drag-to-close: dragging the drawer left past ~30% of its width (or a quick
  // left-flick) dismisses it.
  const DRAWER_WIDTH = 288; // w-72
  const handleDrawerDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -DRAWER_WIDTH * 0.3 || info.velocity.x < -500) {
      setMenuOpen(false);
    }
  };

  return (
    <>
      {/*
        padding-top: env(safe-area-inset-top) pushes content below the iOS notch/
        dynamic island while letting the background bleed behind it — works because
        apple-mobile-web-app-status-bar-style is black-translucent in index.html.
      */}
      <header
        className="sticky top-0 z-40 bg-background/70 backdrop-blur-md backdrop-saturate-150 border-b border-border"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="relative flex items-center h-14 px-3">
          {showBack ? (
            <button
              type="button"
              aria-label="Back"
              onClick={handleBack}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-foreground active:bg-accent focus:outline-none"
            >
              <ChevronLeft size={22} />
            </button>
          ) : (
            <button
              type="button"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-foreground active:bg-accent focus:outline-none"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}

          <span className="absolute inset-x-0 text-center font-semibold text-lg tracking-tight pointer-events-none truncate px-14">
            {resolvedTitle}
          </span>

          <div className="ml-auto flex items-center gap-1">{resolvedTrailing}</div>
        </div>
      </header>

      {!showBack && !menuOpen && (
        <div
          aria-hidden="true"
          onTouchStart={onEdgeTouchStart}
          onTouchMove={onEdgeTouchMove}
          onTouchEnd={onEdgeTouchEnd}
          onTouchCancel={onEdgeTouchEnd}
          className="fixed left-0 top-0 bottom-0 w-5 z-20"
        />
      )}

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-30 bg-black/40"
              onClick={() => setMenuOpen(false)}
            />
            <motion.nav
              key="drawer"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={{ left: 1, right: 0 }}
              dragMomentum={false}
              onDragEnd={handleDrawerDragEnd}
              className="fixed top-0 left-0 bottom-0 z-50 w-72 bg-background shadow-xl flex flex-col touch-pan-y"
              style={{ paddingTop: 'env(safe-area-inset-top)' }}
            >
              <div className="relative flex items-center h-14 px-3 border-b border-border">
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-center w-9 h-9 rounded-lg text-foreground active:bg-accent focus:outline-none"
                >
                  <X size={20} />
                </button>
                <div className="absolute inset-x-0 flex justify-center pointer-events-none">
                  <button
                    type="button"
                    onClick={handleTitleTap}
                    aria-label="Underlings"
                    className="pointer-events-auto bg-transparent border-0 p-1 focus:outline-none"
                  >
                    <img src="https://assets.underlings.com/special/logo-mark.png" alt="Underlings" className="w-7 h-7 object-contain" />
                  </button>
                </div>
                {/* spacer to keep the brand mark centred */}
                <div className="ml-auto w-9 h-9" />
              </div>
              {drawerAccountBlock && (
                <div onClick={() => setMenuOpen(false)} className="border-b border-border">
                  {drawerAccountBlock}
                </div>
              )}
              <nav className="flex-1 overflow-y-auto px-3 py-4">
                {NAV_GROUPS.filter((g) => showDev || g.title !== 'Dev').map((group, idx) => (
                  <div key={idx} className={idx > 0 ? 'mt-5' : ''}>
                    {group.title && (
                      <div className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.title}
                      </div>
                    )}
                    {group.items.map((item) => {
                      if ('action' in item) {
                        const label =
                          syncState === 'running' ? 'Resetting…'
                          : syncState === 'done' ? 'Reset triggered'
                          : syncState === 'error' ? 'Reset failed'
                          : item.label;
                        return (
                          <button
                            key={item.action}
                            type="button"
                            onClick={runFullSync}
                            disabled={syncState === 'running'}
                            className="w-full flex items-center gap-4 px-3 py-3 rounded-lg text-base font-medium text-foreground hover:bg-accent disabled:opacity-60 text-left"
                          >
                            <span className="text-muted-foreground">
                              {syncState === 'running'
                                ? <RefreshCw size={20} className="animate-spin" />
                                : item.icon}
                            </span>
                            {label}
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-4 px-3 py-3 rounded-lg text-base font-medium text-foreground hover:bg-accent"
                        >
                          <span className="text-muted-foreground">{item.icon}</span>
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </nav>
              <div
                className="border-t border-border px-3 py-2"
                style={{ paddingBottom: drawerFooter ? undefined : 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
              >
                <Link
                  to={HOME_ITEM.to}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-4 px-3 py-3 rounded-lg text-base font-medium text-foreground hover:bg-accent"
                >
                  <span className="text-muted-foreground">{HOME_ITEM.icon}</span>
                  {HOME_ITEM.label}
                </Link>
              </div>
              {drawerFooter && (
                <div className="border-t border-border px-3 py-2" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}>
                  {drawerFooter}
                </div>
              )}
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
